// Shared plain-language glossary (SP-2 WS-7, generalized in SP-2b WS-D).
// One term → definition map used by the staff cockpit, the statements and
// fee-review queues, and the TPS portal cycle mirror. Renders as a
// dotted-underline span with the definition on hover + keyboard focus
// (the StatusBadge `hint` pattern: title attribute + tabIndex so keyboard
// users can reach it). `GlossaryText` scans a sentence for known terms and
// wraps each occurrence.

import { Fragment, type ReactNode } from "react";

export const GLOSSARY: Record<string, string> = {
  collected:
    "Money actually received on a loan — recorded servicing payments, net of any that were reversed. Vendor Fee Income is shown as its own stream, never mixed into this total.",
  reversed:
    "A recorded payment that was undone (for example, returned by the bank). It no longer counts toward collected totals, though it stays listed for the record.",
  ICUF:
    "Initial Capital Utilization Fee — Phoenix's recovery of capital it advanced at closing",
  arrears:
    "Older unpaid amounts — the waterfall settles these before the current month",
  NSF: "Non-sufficient funds — the bank returned the payment unpaid",
  "default interest":
    "Extra interest charged while a loan is behind, on top of the regular rate",
  waterfall:
    "The ordered rules that decide where each dollar of a payment goes — fees first, then interest, principal, and the other obligations",
  "notional share":
    "The slice of a payment attributed to a party from their stake, before fees and adjustments",
  "made-whole":
    "An amount owed back to the seller when Phoenix temporarily covers costs from the seller's income — repaid from later payments",
  "prepayment fee":
    "A fee charged when a loan pays off early, compensating for interest that won't be earned",
  YMP: "Yield Maintenance Premium — a payoff charge that makes up the interest the lender would have earned had the loan run to term",
  "autopay draft":
    "An automatic bank withdrawal for the amount due on a statement, started on its due date for borrowers enrolled in autopay",
  "routing number":
    "A 9-digit number that identifies your bank. On a paper check it's the left-most group of numbers along the bottom.",
  "account number":
    "The number that identifies your specific bank account. On a paper check it's the group of numbers to the right of the routing number, along the bottom.",
  "account type":
    "Whether an account is a checking or a savings account — your bank statement or checkbook will say which.",
};

/** Case-tolerant lookup: exact key, initialism uppercase, then lowercase. */
function lookup(term: string): string | undefined {
  return GLOSSARY[term] ?? GLOSSARY[term.toUpperCase()] ?? GLOSSARY[term.toLowerCase()];
}

export function GlossaryHint({
  term,
  children,
}: {
  term: keyof typeof GLOSSARY | string;
  children?: ReactNode;
}) {
  const definition = lookup(term);
  if (!definition) return <>{children ?? term}</>;
  return (
    <span
      className="underline decoration-dotted underline-offset-2 decoration-gray-mid cursor-help"
      title={definition}
      tabIndex={0}
      data-testid={`glossary-${term.toLowerCase()}`}
    >
      {children ?? term}
    </span>
  );
}

// Word-boundary matcher for the glossary vocabulary. ICUF / NSF / YMP are
// initialisms (case-sensitive); phrase terms match sentence or title casing.
// Longer phrases sit before their prefixes so the alternation stays greedy.
const TERM_PATTERN =
  /\b(ICUF|NSF|YMP|[Dd]efault interest|[Nn]otional share|[Mm]ade-whole|[Pp]repayment fee|[Aa]utopay draft|[Ww]aterfall|[Aa]rrears|[Cc]ollected|[Rr]eversed)\b/g;

/** Renders `text` with every known glossary term wrapped in a GlossaryHint. */
export function GlossaryText({ text }: { text: string }) {
  const parts = text.split(TERM_PATTERN);
  if (parts.length === 1) return <>{text}</>;
  return (
    <>
      {parts.map((part, i) =>
        lookup(part) ? (
          <GlossaryHint key={i} term={part.toLowerCase()}>
            {part}
          </GlossaryHint>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        ),
      )}
    </>
  );
}
